// Column widths for the tables of the portals: drag the grip at the right edge of a heading to move the edge between two columns
// (the table keeps its width, one column grows as the next one shrinks); double-click the grip to put the table back.
// The widths are remembered in this browser, per page and table, as shares of the row so they follow the window.
// Works on the grid tables (.pu-table, .hk-tbl: the rows set grid-template-columns from the variable --cols) and on a <table>.
const STORE = 'nc-grid-cols';
const WRAP = '.pu-table, .hk-tbl, table.data-table';
const HEAD = '.pu-th, .hk-tr--h, thead tr';
const MIN = 60;

const readAll = () => { try { return JSON.parse(localStorage.getItem(STORE) || '{}') || {}; } catch { return {}; } };
const writeAll = (all) => { try { localStorage.setItem(STORE, JSON.stringify(all)); } catch { /* not remembered */ } };

function contextOf(el) {
  const head = el.closest(HEAD);
  const wrap = head && head.closest(WRAP);
  return wrap ? { wrap, head, isTable: wrap.tagName === 'TABLE' } : null;
}
const keyOf = ({ head }) => `${window.location.pathname.replace(/\/tenant\/[^/]+/, '/tenant')}|${[...head.classList].filter((c) => c !== 'pu-th' && c !== 'pu-tr').join('.')}|${head.children.length}`;

function setShares({ wrap, head, isTable }, shares) {
  if (isTable) {
    wrap.style.tableLayout = 'fixed';
    Array.from(head.children).forEach((th, i) => { th.style.width = `${(shares[i] * 100).toFixed(2)}%`; });
  } else {
    wrap.style.setProperty('--cols', shares.map((s) => `minmax(0, ${s.toFixed(4)}fr)`).join(' '));
  }
}
function clearShares({ wrap, head, isTable }) {
  if (isTable) {
    wrap.style.tableLayout = '';
    Array.from(head.children).forEach((th) => { th.style.width = ''; });
  } else {
    wrap.style.removeProperty('--cols');
  }
}

// The saved widths of this table, or the table as built when it has none (or when the columns are not the same any more).
export function applyGridColumns(el) {
  const ctx = el && contextOf(el);
  if (!ctx) return;
  const saved = readAll()[keyOf(ctx)];
  if (Array.isArray(saved) && saved.length === ctx.head.children.length) setShares(ctx, saved); else clearShares(ctx);
}

// The widths now, one per heading, in px.
function trackWidths({ head, isTable }) {
  const cells = Array.from(head.children);
  if (isTable) return cells.map((c) => c.getBoundingClientRect().width);
  const tracks = window.getComputedStyle(head).gridTemplateColumns.split(' ').map(parseFloat);
  return tracks.length === cells.length && tracks.every((t) => Number.isFinite(t)) ? tracks : null;
}

export function startGridResize(e) {
  e.preventDefault();
  e.stopPropagation();
  const cell = e.currentTarget.closest('.pu-th > *, .hk-tr--h > *, thead th');
  const ctx = cell && contextOf(cell);
  if (!ctx) return;
  const cells = Array.from(ctx.head.children);
  const i = cells.indexOf(cell);
  const start = trackWidths(ctx);
  // A column with no room (a table that shows only some of its columns in a narrow window) is left as it is.
  if (i < 0 || i >= cells.length - 1 || !start || start[i] < MIN || start[i + 1] < MIN) return;
  const total = start.reduce((a, b) => a + b, 0);
  const startX = e.clientX;
  const prevSelect = document.body.style.userSelect;
  const prevCursor = document.body.style.cursor;
  document.body.style.userSelect = 'none';
  document.body.style.cursor = 'col-resize';
  let shares = start.map((w) => w / total);
  function onMove(ev) {
    const dx = Math.max(MIN - start[i], Math.min(start[i + 1] - MIN, ev.clientX - startX));
    const next = start.slice();
    next[i] = start[i] + dx;
    next[i + 1] = start[i + 1] - dx;
    shares = next.map((w) => w / total);
    setShares(ctx, shares);
  }
  function onUp() {
    document.body.style.userSelect = prevSelect;
    document.body.style.cursor = prevCursor;
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseup', onUp);
    const all = readAll();
    all[keyOf(ctx)] = shares.map((s) => Math.round(s * 10000) / 10000);
    writeAll(all);
  }
  document.addEventListener('mousemove', onMove);
  document.addEventListener('mouseup', onUp);
}

export function resetGridColumns(e) {
  e.preventDefault();
  e.stopPropagation();
  const cell = e.currentTarget.closest('.pu-th > *, .hk-tr--h > *, thead th');
  const ctx = cell && contextOf(cell);
  if (!ctx) return;
  const all = readAll();
  delete all[keyOf(ctx)];
  writeAll(all);
  clearShares(ctx);
}
